import { useEffect, useState } from 'react'
import { Check, Copy, Plus, ShieldOff, Users } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { Card, CardBody } from '../components/ui/Card'
import { Chip } from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Modal, { ConfirmDialog } from '../components/ui/Modal'
import { TextField } from '../components/ui/Field'
import { EmptyState } from '../components/ui/DataTable'
import { SkeletonText } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'

/**
 * Who else can sign in to this panel.
 *
 * Deliberately no self-service signup anywhere in the product — the first
 * platform administrator is bootstrapped once from the console
 * (`artisan platform:admin`), specifically because a tenant-scoped account
 * granting itself platform access would be the same failure mode POSH and
 * whistleblower access is built to prevent, one level up. Every admin after
 * that is added here, by someone who already has access.
 */
export default function Admins() {
  const toast = useToast()

  const [admins, setAdmins] = useState(null)
  const [adding, setAdding] = useState(false)
  const [credential, setCredential] = useState(null)
  const [revoking, setRevoking] = useState(null)
  const [working, setWorking] = useState(false)

  async function load() {
    try {
      setAdmins(await api.get('/platform/admins'))
    } catch (err) {
      toast.error(err.message)
      setAdmins([])
    }
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function confirmRevoke() {
    setWorking(true)
    try {
      await api.post(`/platform/admins/${revoking.id}/deactivate`)
      toast.success(`${revoking.name}'s access revoked.`)
      setRevoking(null)
      load()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-semibold text-[var(--color-ink-900)]">
            Platform administrators
          </h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-500)]">
            Everyone who can sign in here and manage client organisations.
          </p>
        </div>

        <Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>
          Add administrator
        </Button>
      </div>

      <Card>
        <CardBody padded={false}>
          {admins === null ? (
            <div className="p-5"><SkeletonText lines={4} /></div>
          ) : admins.length === 0 ? (
            <EmptyState icon={Users} title="No other administrators" />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {admins.map((admin) => (
                <li key={admin.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-[var(--color-ink-900)]">{admin.name}</span>
                      {admin.is_you && <Chip tone="info">You</Chip>}
                      {!admin.is_active && <Chip tone="warning">Revoked</Chip>}
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--color-ink-500)]">
                      {admin.email}
                      {admin.last_login_at && ` · last signed in ${formatDateTime(admin.last_login_at)}`}
                    </p>
                  </div>

                  {admin.is_active && !admin.is_you && (
                    <Button size="sm" variant="ghost" icon={ShieldOff} onClick={() => setRevoking(admin)}>
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {adding && (
        <AddAdminDialog
          onClose={() => setAdding(false)}
          onDone={(cred) => { setAdding(false); load(); setCredential(cred) }}
          toast={toast}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} toast={toast} />
      )}

      <ConfirmDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        onConfirm={confirmRevoke}
        title="Revoke platform access?"
        message={revoking ? `${revoking.name} will no longer be able to sign in here, and their current session is ended immediately.` : ''}
        confirmLabel="Revoke"
        destructive
        loading={working}
      />
    </div>
  )
}

function AddAdminDialog({ onClose, onDone, toast }) {
  const [form, setForm] = useState({ name: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  async function submit() {
    setSubmitting(true)
    setError(null)

    try {
      const result = await api.post('/platform/admins', form)
      toast.success('Administrator added.')
      onDone({ email: result.email, password: result.password })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a platform administrator"
      dismissable={!submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={submitting} disabled={!form.name.trim() || !form.email.trim()}>
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-md border px-3 py-2.5 text-sm"
            style={{ borderColor: 'var(--color-danger)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
          >
            {error}
          </div>
        )}

        <TextField
          label="Name"
          required
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <TextField
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />

        <p className="text-xs leading-relaxed text-[var(--color-ink-500)]">
          A generated password will be shown once on the next screen. They will be required to
          change it the first time they sign in.
        </p>
      </div>
    </Modal>
  )
}

function CredentialDialog({ credential, onClose, toast }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(credential.password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Could not copy — select and copy the password manually.')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Administrator added"
      size="sm"
      dismissable={false}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <div
          role="alert"
          className="rounded-md border px-3 py-2.5 text-sm leading-relaxed"
          style={{ borderColor: 'var(--color-warning)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-ink-900)' }}
        >
          This password is shown <strong>once</strong>. Copy it now — it cannot be retrieved again,
          only reset by them at their next sign-in.
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">Email</p>
          <p className="mt-0.5 font-mono text-sm text-[var(--color-ink-900)]">{credential.email}</p>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">Password</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 rounded-md border border-[var(--color-line-strong)] bg-[var(--color-surface-sunken)] px-3 py-2 font-mono text-sm text-[var(--color-ink-900)]">
              {credential.password}
            </code>
            <Button size="sm" variant="secondary" icon={copied ? Check : Copy} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
